/**
 * PaymentMethodsSettings — Editor de etiquetas y disponibilidad de los métodos
 * de pago configurables del negocio.
 *
 * QUÉ HACE:
 *  - Lista todos los métodos del negocio (efectivo, tarjeta, digitales).
 *  - Permite renombrar los digitales (ej: "Nequi" → "Bizum" / "Pix") sin
 *    cambiar el ID interno: las ventas históricas siguen apuntando al mismo
 *    ID y se muestran con el snapshot guardado en la venta.
 *  - Permite habilitar/desactivar Tarjeta y los slots digitales. Efectivo es
 *    el único método obligatorio.
 *  - Persiste vía `paymentMethodsConfig` y dispara el evento global para que
 *    la vista de cobro se actualice en caliente.
 *
 * QUÉ NO DEBE HACER:
 *  - NO toca ventas existentes ni modifica IDs.
 *  - NO impone nombres por país: el dueño decide las etiquetas.
 *  - NO procesa lógica de cobro.
 *
 * Pensado para vivir dentro de "Opciones avanzadas" de la pantalla
 * «POS, gaveta y seguridad» (junto con `CashDrawerSecuritySettings`).
 */
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  Banknote,
  CreditCard,
  Smartphone,
  Info,
  RefreshCw,
} from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { FloatingLabelInput } from "@/components/ui/FloatingLabelInput";
import { Switch } from "@/components/ui/Switch";
import {
  DEFAULT_PAYMENT_METHODS,
  loadPaymentMethodsConfig,
  normalizePaymentMethodsConfig,
  persistPaymentMethodsConfig,
  type PaymentMethodSetting,
  type PaymentMethodsConfig,
  type UsageFeePaidBy,
} from "@/lib/paymentMethodsConfig";

const SECTION_LABEL =
  "text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-slate-500 mb-2 px-0.5";

/** Icono coherente con el resto del POS según la categoría del método. */
function MethodIcon({ kind }: { kind: PaymentMethodSetting["kind"] }) {
  if (kind === "efectivo") {
    return (
      <Banknote className="text-green-700 dark:text-green-300" size={16} />
    );
  }
  if (kind === "tarjeta") {
    return (
      <CreditCard className="text-purple-700 dark:text-purple-300" size={16} />
    );
  }
  return <Smartphone className="text-blue-700 dark:text-blue-300" size={16} />;
}

function UsageFeeEditor({
  method,
  disabled,
  onChange,
}: {
  method: PaymentMethodSetting;
  disabled?: boolean;
  onChange: (id: string, pct: number, paidBy: UsageFeePaidBy | null) => void;
}) {
  if (method.kind === "efectivo") return null;

  const pct = method.usageFee?.pct ?? 0;
  const paidBy = method.usageFee?.paidBy ?? "negocio";
  const enabled = pct > 0;

  return (
    <div className="mt-2 pt-2 border-t border-gray-100 dark:border-slate-700/80 space-y-2">
      <p className="text-[9px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500">
        Tarifa de uso (pasarela)
      </p>
      <div className="flex items-center gap-2">
        <Switch
          size="sm"
          checked={enabled}
          disabled={disabled}
          onChange={(v) => onChange(method.id, v ? pct || 1.5 : 0, paidBy)}
          ariaLabel={`Tarifa ${method.label}`}
        />
        <span className="text-[10px] text-gray-500 dark:text-slate-400">
          {enabled ? "Activa" : "Sin tarifa"}
        </span>
      </div>
      {enabled ? (
        <>
          <FloatingLabelInput
            label="Porcentaje (%)"
            hint="Ej: 1,5 = comisión Nequi/tarjeta"
            type="number"
            inputMode="decimal"
            min={0.01}
            max={100}
            step={0.01}
            disabled={disabled}
            value={String(pct)}
            onChange={(e) => {
              const n = parseFloat(e.target.value.replace(",", "."));
              onChange(method.id, Number.isFinite(n) ? n : 0, paidBy);
            }}
            className="!text-xs"
          />
          <div className="grid grid-cols-2 gap-1.5">
            {(
              [
                {
                  id: "negocio" as const,
                  label: "La paga el negocio",
                  hint: "El cliente paga solo el precio; el negocio asume la comisión.",
                },
                {
                  id: "cliente" as const,
                  label: "La paga el cliente",
                  hint: "El cliente paga el precio más la comisión; al negocio le llega el precio completo.",
                },
              ] as const
            ).map((opt) => (
              <button
                key={opt.id}
                type="button"
                disabled={disabled}
                onClick={() => onChange(method.id, pct, opt.id)}
                className={`text-left rounded-lg border px-2 py-1.5 transition-colors ${
                  paidBy === opt.id
                    ? "border-primary-400 bg-primary-50 dark:bg-primary-950/30 ring-1 ring-primary-200 dark:ring-primary-800"
                    : "border-gray-200 dark:border-slate-600 hover:border-gray-300"
                }`}
              >
                <p className="text-[10px] font-bold text-gray-800 dark:text-slate-100">
                  {opt.label}
                </p>
                <p className="text-[9px] text-gray-400 dark:text-slate-500 leading-snug mt-0.5">
                  {opt.hint}
                </p>
              </button>
            ))}
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50/70 px-2 py-1.5 leading-snug text-amber-900 dark:border-amber-800 dark:bg-amber-950/25 dark:text-amber-100">
            <p className="text-[8px] font-black uppercase tracking-wide">
              ¿Quién paga la comisión? Así funciona
            </p>
            <p className="mt-1 text-[9px]">
              <strong>La paga el negocio:</strong> el cliente paga solo el
              precio. Tú asumes la comisión del medio de pago (por ejemplo,
              tarjeta): sale de tu parte y en el cierre ves el dinero real que
              te quedó.
            </p>
            <p className="mt-1 text-[9px]">
              <strong>La paga el cliente:</strong> el precio sube lo justo para
              cubrir la comisión. Ejemplo: una venta de $14.000 pagada con
              tarjeta y comisión de 1,5 % → el cliente paga $14.214. El medio de
              pago se queda su parte (≈$213 o $214) y a ti te llegan tus $14.000
              completos (a veces $1 de más). Nunca te llega menos.
            </p>
            <p className="mt-1 text-[9px]">
              <strong>La fórmula (para que nunca falte):</strong> Total = precio
              ÷ 0,985 → $14.000 ÷ 0,985 = $14.214 (redondeado hacia arriba). No
              se multiplica el precio por 1,015 (eso daría $14.210): multiplicar
              cobraría la comisión también sobre la propia comisión (comisión
              sobre comisión) y el negocio perdería unos $3 en cada venta. Al
              dividir, la comisión queda cubierta por completo.
            </p>
          </div>
        </>
      ) : null}
    </div>
  );
}

function IncentiveEditor({
  method,
  disabled,
  onChange,
}: {
  method: PaymentMethodSetting;
  disabled?: boolean;
  onChange: (id: string, pct: number) => void;
}) {
  const pct = method.incentivePct ?? 0;
  const enabled = pct > 0;

  return (
    <div className="mt-2 pt-2 border-t border-gray-100 dark:border-slate-700/80 space-y-2">
      <p className="text-[9px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500">
        Incentivo (descuento al cliente)
      </p>
      <div className="flex items-center gap-2">
        <Switch
          size="sm"
          checked={enabled}
          disabled={disabled}
          onChange={(v) => onChange(method.id, v ? pct || 10 : 0)}
          ariaLabel={`Incentivo ${method.label}`}
        />
        <span className="text-[10px] text-gray-500 dark:text-slate-400">
          {enabled ? "Activo" : "Sin incentivo"}
        </span>
      </div>
      {enabled ? (
        <>
          <FloatingLabelInput
            label="Porcentaje (%)"
            hint="Ej: 10 = 10% de descuento pagando con este método"
            type="number"
            inputMode="decimal"
            min={0.01}
            max={100}
            step={0.5}
            disabled={disabled}
            value={String(pct)}
            onChange={(e) => {
              const n = parseFloat(e.target.value.replace(",", "."));
              onChange(method.id, Number.isFinite(n) ? n : 0);
            }}
            className="!text-xs"
          />
          <p className="px-0.5 text-[9px] text-gray-500 dark:text-slate-400 leading-snug">
            El cliente paga menos al elegir este método. Aplica en Pago Simple
            (caja y tienda); en efectivo el total se ajusta a la menor
            denominación para que el cambio sea exacto.
          </p>
        </>
      ) : null}
    </div>
  );
}

export function PaymentMethodsSettings() {
  const businessId = useAuthStore((s) => s.user?.businessId);
  const [cfg, setCfg] = useState<PaymentMethodsConfig>(() =>
    loadPaymentMethodsConfig(businessId),
  );
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(false);
    setCfg(loadPaymentMethodsConfig(businessId));
    setHydrated(true);
  }, [businessId]);

  useEffect(() => {
    const onExternal = () => setCfg(loadPaymentMethodsConfig(businessId));
    window.addEventListener("altenot:payment-methods-config", onExternal);
    return () =>
      window.removeEventListener("altenot:payment-methods-config", onExternal);
  }, [businessId]);

  // Auto-persistencia con debounce: el dueño no tiene que pulsar "Guardar".
  useEffect(() => {
    if (!hydrated) return;
    const id = window.setTimeout(() => {
      const next = normalizePaymentMethodsConfig(cfg);
      const prev = normalizePaymentMethodsConfig(
        loadPaymentMethodsConfig(businessId),
      );
      const stripUpdatedAt = (c: PaymentMethodsConfig) =>
        JSON.stringify(c.methods);
      if (stripUpdatedAt(prev) === stripUpdatedAt(next)) return;
      persistPaymentMethodsConfig(businessId, next);
    }, 420);
    return () => window.clearTimeout(id);
  }, [businessId, cfg, hydrated]);

  const updateLabel = (id: string, label: string) => {
    setCfg((c) => ({
      ...c,
      methods: c.methods.map((m) => (m.id === id ? { ...m, label } : m)),
    }));
  };

  const updateUsageFee = (
    id: string,
    pct: number,
    paidBy: UsageFeePaidBy | null,
  ) => {
    setCfg((c) => ({
      ...c,
      methods: c.methods.map((m) => {
        if (m.id !== id || m.kind === "efectivo") return m;
        if (!pct || pct <= 0) {
          const { usageFee: _removed, ...rest } = m;
          return rest;
        }
        return {
          ...m,
          usageFee: { pct, paidBy: paidBy ?? "negocio" },
        };
      }),
    }));
  };

  const updateIncentive = (id: string, pct: number) => {
    setCfg((c) => ({
      ...c,
      methods: c.methods.map((m) =>
        m.id === id
          ? {
              ...m,
              incentivePct:
                pct > 0
                  ? Math.min(100, Math.max(0, Math.round(pct * 100) / 100))
                  : null,
            }
          : m,
      ),
    }));
  };

  const updateEnabled = (id: string, enabled: boolean) => {
    setCfg((c) => {
      const next = c.methods.map((m) =>
        m.id === id && !m.locked ? { ...m, enabled } : m,
      );
      return { ...c, methods: next };
    });
    if (!enabled) {
      queueMicrotask(() =>
        toast(
          "Método desactivado: ya no aparecerá en nuevas ventas. Las ventas históricas siguen mostrándolo con su nombre original.",
          { duration: 4500, id: `pm-${id}-off` },
        ),
      );
    }
  };

  const resetDefaults = useCallback(() => {
    setCfg({
      methods: DEFAULT_PAYMENT_METHODS.map((m) => ({ ...m })),
      updatedAt: Date.now(),
    });
    queueMicrotask(() =>
      toast.success("Etiquetas restauradas a los valores por defecto."),
    );
  }, []);

  // Solo mostramos los slots fijos (efectivo, tarjeta) y los 4 slots digitales.
  // Si quedaron IDs legacy en el config (de ventas viejas), se mantienen en
  // localStorage para que el resolver los encuentre, pero NO se editan aquí.
  const visibleMethods = cfg.methods.filter(
    (m) =>
      m.id === "efectivo" || m.id === "tarjeta" || m.id.startsWith("digital_"),
  );

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 rounded-xl border border-blue-200/70 dark:border-blue-900/50 bg-blue-50/80 dark:bg-blue-950/30 p-3">
        <Info
          className="text-blue-600 dark:text-blue-400 shrink-0 mt-0.5"
          size={14}
        />
        <p className="text-[11px] text-blue-900 dark:text-blue-100 leading-snug">
          <span className="font-bold">¿Cómo funciona?</span> Cambiar el nombre
          (ej: «Nequi» → «Bizum») NO afecta a las ventas ya cobradas. Configura
          tarifas de pasarela (ej: 1,5 %) para que el cierre muestre el neto
          real en cuenta y el cuadre digital no descuadre. Activa un{" "}
          <strong>Incentivo</strong> (ej: 10 %) para premiar a tus clientes que
          paguen con un método: el descuento se aplica en vivo en caja, tienda
          virtual y ticket.
        </p>
      </div>

      <div>
        <p className={SECTION_LABEL}>Método obligatorio</p>
        <div className="space-y-2">
          {visibleMethods
            .filter((m) => m.kind === "efectivo")
            .map((m) => (
              <div
                key={m.id}
                className="p-2 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50/60 dark:bg-slate-800/60"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-white dark:bg-slate-900 flex items-center justify-center border border-gray-200 dark:border-slate-600 shrink-0">
                    <MethodIcon kind={m.kind} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500">
                      {m.kind === "efectivo" ? "Efectivo" : "Tarjeta"}{" "}
                      <span className="font-mono text-[9px] text-gray-300 dark:text-slate-600">
                        · {m.id}
                      </span>
                    </p>
                    <p className="text-xs font-bold text-gray-800 dark:text-slate-100 truncate">
                      {m.label}
                    </p>
                  </div>
                </div>
                <IncentiveEditor method={m} onChange={updateIncentive} />
              </div>
            ))}
        </div>
      </div>

      <div>
        <p className={SECTION_LABEL}>Método base opcional</p>
        <div className="space-y-2">
          {visibleMethods
            .filter((m) => m.kind === "tarjeta")
            .map((m) => (
              <div
                key={m.id}
                className={`p-2 rounded-xl border ${
                  m.enabled
                    ? "border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900"
                    : "border-gray-200/70 dark:border-slate-700/70 bg-gray-50/60 dark:bg-slate-800/40"
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-white dark:bg-slate-900 flex items-center justify-center border border-gray-200 dark:border-slate-600 shrink-0">
                    <MethodIcon kind={m.kind} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500">
                      Tarjeta{" "}
                      <span className="font-mono text-[9px] text-gray-300 dark:text-slate-600">
                        · {m.id}
                      </span>
                    </p>
                    <p className="text-xs font-bold text-gray-800 dark:text-slate-100 truncate">
                      {m.label}
                    </p>
                  </div>
                  <Switch
                    size="sm"
                    checked={m.enabled}
                    onChange={(value) => updateEnabled(m.id, value)}
                    ariaLabel="Mostrar Tarjeta en cobros"
                  />
                </div>
                <UsageFeeEditor
                  method={m}
                  disabled={!m.enabled}
                  onChange={updateUsageFee}
                />
                <IncentiveEditor
                  method={m}
                  disabled={!m.enabled}
                  onChange={updateIncentive}
                />
              </div>
            ))}
        </div>
      </div>

      <div>
        <p className={SECTION_LABEL}>Métodos digitales (editables)</p>
        <div className="space-y-2">
          {visibleMethods
            .filter((m) => m.kind === "digital")
            .map((m, idx) => (
              <div
                key={m.id}
                className={`p-2 rounded-xl border ${
                  m.enabled
                    ? "border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900"
                    : "border-gray-200/70 dark:border-slate-700/70 bg-gray-50/60 dark:bg-slate-800/40"
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center shrink-0">
                    <MethodIcon kind={m.kind} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500">
                      Digital {idx + 1}{" "}
                      <span className="font-mono text-[9px] text-gray-300 dark:text-slate-600">
                        · {m.id}
                      </span>
                    </p>
                  </div>
                  <Switch
                    size="sm"
                    checked={m.enabled}
                    onChange={(v) => updateEnabled(m.id, v)}
                    ariaLabel={`Activar ${m.label}`}
                  />
                </div>
                <div className="mt-2">
                  <FloatingLabelInput
                    label="Nombre que verá el cajero"
                    hint="Ej: Nequi, Bizum, Pix, Mercado Pago…"
                    type="text"
                    autoComplete="off"
                    maxLength={24}
                    value={m.label}
                    disabled={!m.enabled}
                    onChange={(e) => updateLabel(m.id, e.target.value)}
                    className="!text-xs"
                  />
                </div>
                <UsageFeeEditor
                  method={m}
                  disabled={!m.enabled}
                  onChange={updateUsageFee}
                />
                <IncentiveEditor
                  method={m}
                  disabled={!m.enabled}
                  onChange={updateIncentive}
                />
              </div>
            ))}
        </div>
      </div>

      <button
        type="button"
        onClick={resetDefaults}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-gray-300 dark:border-slate-600 text-[11px] font-bold text-gray-500 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 hover:border-primary-400 transition-colors"
      >
        <RefreshCw size={13} />
        Restaurar nombres por defecto
      </button>
    </div>
  );
}
